if (!customElements.get('reise-pdp-content')) {
  customElements.define('reise-pdp-content', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.resizeObservers = [];
      this.setupLightbox(signal);
      this.setupReviews(signal);
      this.setupAttributes(signal);

      this.querySelectorAll('[data-rpc-carousel]').forEach((carousel) => {
        const track = carousel.querySelector('[data-rpc-track]');
        const navigation = carousel.querySelector('[data-rpc-carousel-nav]');
        const previous = carousel.querySelector('[data-rpc-prev]');
        const next = carousel.querySelector('[data-rpc-next]');
        if (!track || !navigation || !previous || !next) return;

        const update = () => {
          const maximum = track.scrollWidth - track.clientWidth;
          navigation.hidden = maximum <= 2;
          previous.disabled = track.scrollLeft <= 2;
          next.disabled = track.scrollLeft >= maximum - 2;
        };
        const scroll = (direction) => {
          const first = track.firstElementChild;
          const step = first ? first.getBoundingClientRect().width + 4 : track.clientWidth;
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          track.scrollBy({ left: step * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
        };

        previous.addEventListener('click', () => scroll(-1), { signal });
        next.addEventListener('click', () => scroll(1), { signal });
        track.addEventListener('scroll', update, { passive: true, signal });
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(track);
        this.resizeObservers.push(resizeObserver);
        update();
      });

      const recommendations = this.querySelector('[data-rpc-recommendations-url]');
      if (recommendations) {
        this.recommendationObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.recommendationObserver.disconnect();
          this.loadRecommendations(recommendations, signal);
        }, { rootMargin: '600px 0px' });
        this.recommendationObserver.observe(this);
      }
    }

    setupAttributes(signal) {
      const desktop = window.matchMedia('(min-width: 1000px)');
      const opened = () => this.querySelectorAll('.rpc-attributes details[open]');
      document.addEventListener('click', (event) => {
        if (!desktop.matches) return;
        opened().forEach((details) => { if (!details.contains(event.target)) details.open = false; });
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !desktop.matches) return;
        const details = event.target.closest?.('.rpc-attributes details[open]');
        if (!details) return;
        details.open = false;
        details.querySelector('summary')?.focus();
      }, { signal });
    }

    disconnectedCallback() {
      this.querySelector('[data-rpc-lightbox]')?.close();
      this.cleanupLightbox?.(false);
      this.querySelector('[data-rpc-reviews-dialog]')?.close();
      this.controller?.abort();
      this.reviewObserver?.disconnect();
      this.recommendationObserver?.disconnect();
      this.resizeObservers?.forEach((observer) => observer.disconnect());
    }

    setupReviews(signal) {
      const section = this.querySelector('.rpc-reviews');
      const widget = section?.querySelector('[data-rpc-review-widget]');
      const summary = section?.querySelector('[data-rpc-review-summary]');
      const cards = section?.querySelector('[data-rpc-review-cards]');
      const open = section?.querySelector('[data-rpc-reviews-open]');
      const dialog = section?.querySelector('[data-rpc-reviews-dialog]');
      if (!widget || !summary || !cards || !open || !dialog) return;
      const element = (tag, className, text) => {
        const node = document.createElement(tag);
        node.className = className;
        if (text !== undefined) node.textContent = text;
        return node;
      };
      let lastSignature = '';
      const render = () => {
        const source = widget.querySelector('.jdgm-rev-widg[data-average-rating][data-number-of-reviews]');
        const reviews = [...widget.querySelectorAll('.jdgm-rev')].slice(0, 4);
        const average = Number(source?.dataset.averageRating);
        const total = Number(source?.dataset.numberOfReviews);
        const write = source?.querySelector('.jdgm-write-rev-link');
        if (source && total === 0) {
          if (lastSignature !== 'empty') {
            summary.replaceChildren(element('p', 'rpc-review-empty-message', 'Este produto ainda não tem relatos. Seja o primeiro a contar como foi.'));
            lastSignature = 'empty';
          }
          summary.hidden = false;
          cards.hidden = open.hidden = true;
          section.classList.remove('rpc-reviews--ready');
          section.classList.add('rpc-reviews--empty');
          if (dialog.contains(widget)) dialog.before(widget);
          if (write && write.textContent !== 'Conte sua experiência') {
            write.dataset.rpcOriginalLabel ||= write.textContent;
            write.textContent = 'Conte sua experiência';
          }
          return;
        }
        if (section.classList.contains('rpc-reviews--empty')) {
          section.classList.remove('rpc-reviews--empty');
          summary.hidden = true;
          if (write?.dataset.rpcOriginalLabel) write.textContent = write.dataset.rpcOriginalLabel;
        }
        if (!source || !reviews.length || !(total > 0) || !(average > 0 && average <= 5)) return;
        const data = reviews.map((review) => ({
          id: review.dataset.reviewId,
          text: review.querySelector('.jdgm-rev__body')?.textContent.trim(),
          author: review.querySelector('.jdgm-rev__author')?.textContent.trim(),
          score: Number(review.querySelector('.jdgm-rev__rating')?.dataset.score),
          verified: review.dataset.verifiedBuyer === 'true'
        }));
        if (data.some((review) => !review.text || !review.author || !(review.score >= 1 && review.score <= 5))) return;
        const histogram = [...source.querySelectorAll('.jdgm-histogram__row[data-rating]')].map((row) => ({
          rating: Number(row.dataset.rating),
          percentage: Number(row.dataset.percentage)
        })).filter((row) => row.rating >= 1 && row.rating <= 5 && row.percentage >= 0 && row.percentage <= 100);
        const signature = JSON.stringify({ average, total, data, histogram });
        if (signature === lastSignature) return;
        lastSignature = signature;
        const score = element('div', 'rpc-review-score-block');
        score.append(element('span', 'rpc-review-score', average.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })), element('span', 'rpc-review-scale', ' / 5'), element('span', 'rpc-review-total', `${total.toLocaleString('pt-BR')} avaliações`));
        const chart = element('div', 'rpc-review-chart');
        histogram.forEach((entry) => {
          const row = element('div', 'rpc-review-row');
          const track = element('span', 'rpc-review-track');
          const fill = element('i', '');
          fill.style.width = `${entry.percentage}%`;
          track.append(fill);
          row.append(element('span', '', `${entry.rating} ★`), track, element('span', '', `${Math.round(entry.percentage)}%`));
          chart.append(row);
        });
        summary.replaceChildren(score, chart);
        cards.replaceChildren(...data.map((review) => {
          const card = element('article', 'rpc-review-card');
          const stars = element('span', 'rpc-review-stars', '★'.repeat(review.score) + '☆'.repeat(5 - review.score));
          stars.setAttribute('role', 'img');
          stars.setAttribute('aria-label', `${review.score} de 5 estrelas`);
          const meta = element('p', 'rpc-review-meta');
          meta.append(element('strong', '', review.author));
          if (review.verified) meta.append(document.createTextNode(' · Compra verificada'));
          card.append(stars, element('p', 'rpc-review-text', review.text), meta);
          return card;
        }));
        open.textContent = `Ver todas as ${total.toLocaleString('pt-BR')} avaliações`;
        summary.hidden = false;
        cards.hidden = false;
        open.hidden = false;
        section.classList.add('rpc-reviews--ready');
        if (!dialog.contains(widget)) dialog.querySelector('[data-rpc-reviews-dialog-content]').append(widget);
      };
      this.reviewObserver = new MutationObserver(render);
      this.reviewObserver.observe(widget, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-average-rating', 'data-number-of-reviews', 'data-percentage'] });
      render();
      const close = dialog.querySelector('[data-rpc-reviews-close]');
      let scrollLock;
      let returnFocus = true;
      const unlockScroll = () => {
        if (!scrollLock) return;
        document.body.style.overflow = scrollLock.body;
        document.documentElement.style.overflow = scrollLock.html;
        scrollLock = null;
      };
      open.addEventListener('click', () => {
        if (dialog.open) return;
        returnFocus = true;
        if (!scrollLock) scrollLock = { body: document.body.style.overflow, html: document.documentElement.style.overflow };
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        dialog.showModal();
        dialog.querySelector('[data-rpc-reviews-dialog-content]').scrollTop = 0;
        close.focus({ preventScroll: true });
      }, { signal });
      close.addEventListener('click', () => dialog.close(), { signal });
      dialog.addEventListener('click', (event) => {
        if (!event.target.closest('.jdgm-write-rev-link')) return;
        const nativeForm = document.querySelector('.jdgm-write-review-modal');
        if (!nativeForm || dialog.contains(nativeForm)) return;
        // Judge.me opens its own overlay outside the native dialog's top layer.
        returnFocus = false;
        unlockScroll();
        dialog.close();
      }, { capture: true, signal });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      }, { signal });
      dialog.addEventListener('close', () => {
        if (dialog.open) return;
        unlockScroll();
        if (returnFocus && open.isConnected) open.focus({ preventScroll: true });
      }, { signal });
      signal.addEventListener('abort', () => {
        if (dialog.open) dialog.close();
        unlockScroll();
      }, { once: true });
    }

    setupLightbox(signal) {
      const dialog = this.querySelector('[data-rpc-lightbox]');
      const photos = [...this.querySelectorAll('[data-rpc-photo]')];
      if (!dialog || !photos.length) return;
      const stage = dialog.querySelector('[data-rpc-lightbox-stage]');
      const side = dialog.querySelector('[data-rpc-post-side]');
      const count = dialog.querySelector('[data-rpc-photo-count]');
      const previous = dialog.querySelector('[data-rpc-photo-prev]');
      const next = dialog.querySelector('[data-rpc-photo-next]');
      if (!stage || !side || !count || !previous || !next) return;
      const playButton = dialog.querySelector('[data-rpc-reel-play]');
      const muteButton = dialog.querySelector('[data-rpc-reel-mute]');
      const progress = dialog.querySelector('[data-rpc-reel-progress]');
      const reelControls = dialog.querySelectorAll('.rpc-reel-controls, .rpc-reel-progress-wrapper, .rpc-reel-hint');
      const desktop = window.matchMedia('(min-width: 1000px)');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const interactive = 'a, button, input, textarea, select, label, [contenteditable]:not([contenteditable="false"]), [role="button"], [role="textbox"]';
      const pointers = new Set();
      let current = 0;
      let opener;
      let scrollLock;
      let mediaController;
      let originalLoop = false;
      let mutedPreference;
      let muteChosen = false;
      let reel = false;
      let gesture;
      let multiplePointers = false;
      let ignoreClickUntil = 0;
      let animation;
      const video = () => stage.querySelector('video');
      const focusClose = () => side.querySelector('[data-rpc-lightbox-close]')?.focus({ preventScroll: true });
      const resetMotion = () => {
        animation?.cancel();
        animation = null;
        const media = stage.firstElementChild;
        if (media) {
          media.style.removeProperty('transform');
          media.style.removeProperty('opacity');
        }
        gesture = null;
        pointers.forEach((id) => {
          if (stage.hasPointerCapture(id)) stage.releasePointerCapture(id);
        });
        pointers.clear();
        multiplePointers = false;
      };
      const syncControls = () => {
        const media = video();
        const paused = !media || media.paused;
        dialog.classList.toggle('is-paused', !!media && paused);
        dialog.classList.toggle('is-muted', !!media && media.muted);
        if (playButton) playButton.setAttribute('aria-label', paused ? playButton.dataset.playLabel : playButton.dataset.pauseLabel);
        if (muteButton) {
          muteButton.setAttribute('aria-label', media?.muted ? muteButton.dataset.unmuteLabel : muteButton.dataset.muteLabel);
          muteButton.setAttribute('aria-pressed', String(!!media?.muted));
        }
        if (progress) {
          const duration = media?.duration;
          progress.disabled = !(duration > 0 && Number.isFinite(duration));
          progress.value = progress.disabled ? 0 : Math.min(100, media.currentTime / duration * 100);
          progress.style.setProperty('--rpc-reel-progress', `${progress.value}%`);
        }
      };
      const setMode = () => {
        const media = video();
        reel = !!media && !desktop.matches;
        dialog.classList.toggle('rpc-post--reel', reel);
        reelControls.forEach((control) => {
          control.hidden = !reel || (control.classList.contains('rpc-reel-hint') && photos.length < 2);
        });
        if (media) {
          media.controls = !reel;
          media.loop = reel || originalLoop;
        }
        if (!reel && [...reelControls].some((control) => control.contains(document.activeElement))) focusClose();
        syncControls();
      };
      const releaseMedia = () => {
        mediaController?.abort();
        mediaController = null;
        stage.querySelectorAll('video').forEach((media) => {
          media.pause();
          media.removeAttribute('autoplay');
          media.removeAttribute('src');
          media.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
          media.load();
        });
      };
      const playVideo = () => {
        const media = video();
        if (!media || !dialog.open) return;
        media.play().catch((error) => {
          if (media !== video() || !dialog.open) return;
          if (error.name === 'NotAllowedError' && !media.muted) {
            mutedPreference = media.muted = true;
            media.play().catch(syncControls);
          } else syncControls();
        });
      };
      const togglePlayback = () => {
        const media = video();
        if (!media) return;
        if (media.paused) playVideo();
        else media.pause();
      };
      this.cleanupLightbox = (restoreFocus = true) => {
        resetMotion();
        releaseMedia();
        stage.replaceChildren();
        side.replaceChildren();
        setMode();
        if (scrollLock) {
          document.body.style.overflow = scrollLock.body;
          document.documentElement.style.overflow = scrollLock.html;
          scrollLock = null;
        }
        if (restoreFocus && opener?.isConnected) opener.focus({ preventScroll: true });
        opener = null;
      };
      const show = (index, play = false, direction = 0, offset = 0) => {
        const nextIndex = (index % photos.length + photos.length) % photos.length;
        const photo = photos[nextIndex];
        const template = photo.closest('.rpc-lifestyle-item')?.querySelector('template[data-rpc-post-template]');
        const fragment = template?.content.cloneNode(true);
        const media = fragment?.querySelector('[data-rpc-post-image]');
        const content = fragment?.querySelector('[data-rpc-post-content]');
        if (!media || !content) return false;
        const restoreFocus = side.contains(document.activeElement);
        resetMotion();
        releaseMedia();
        current = nextIndex;
        if (media.tagName === 'VIDEO') {
          originalLoop = media.loop;
          media.playsInline = true;
          media.preload = 'metadata';
          media.removeAttribute('autoplay');
          if (!muteChosen) mutedPreference = !desktop.matches || media.muted;
          media.muted = mutedPreference;
          mediaController = new AbortController();
          const mediaSignal = mediaController.signal;
          ['play', 'pause', 'ended', 'timeupdate', 'durationchange', 'loadedmetadata'].forEach((type) => {
            media.addEventListener(type, syncControls, { signal: mediaSignal });
          });
          media.addEventListener('volumechange', () => {
            mutedPreference = media.muted;
            syncControls();
          }, { signal: mediaSignal });
        } else {
          media.alt = media.alt || photo.dataset.alt || '';
          media.loading = 'eager';
        }
        stage.replaceChildren(media);
        side.replaceChildren(...content.childNodes);
        count.textContent = `${current + 1} / ${photos.length}`;
        setMode();
        if (media.tagName === 'VIDEO') media.load();
        if (restoreFocus && dialog.open) focusClose();
        // Keep play() in the initiating gesture for browsers requiring user activation.
        if (play) playVideo();
        if (reel && direction && !reducedMotion.matches) {
          const distance = stage.clientHeight || window.innerHeight;
          animation = media.animate([
            { transform: `translate3d(0, ${direction * distance + offset}px, 0)`, opacity: 0.65 },
            { transform: 'translate3d(0, 0, 0)', opacity: 1 }
          ], { duration: 220, easing: 'cubic-bezier(.22,.61,.36,1)' });
          const incoming = animation;
          incoming.finished.catch(() => {}).then(() => { if (animation === incoming) animation = null; });
        }
        return true;
      };
      const navigate = (direction, offset = 0) => {
        if (photos.length > 1) show(current + direction, true, reel ? direction : 0, offset);
      };
      previous.hidden = next.hidden = photos.length < 2;
      photos.forEach((photo, index) => photo.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        if (!dialog.open && scrollLock) this.cleanupLightbox(false);
        if (!show(index)) return;
        event.preventDefault();
        opener = photo;
        const product = this.closest('reise-product') || document.querySelector('reise-product');
        product?.querySelectorAll('video').forEach((media) => {
          if (!dialog.contains(media)) media.pause();
        });
        if (!dialog.open) {
          scrollLock = { body: document.body.style.overflow, html: document.documentElement.style.overflow };
          document.body.style.overflow = 'hidden';
          document.documentElement.style.overflow = 'hidden';
          dialog.showModal();
        }
        focusClose();
        playVideo();
      }, { signal }));
      previous.addEventListener('click', () => navigate(-1), { signal });
      next.addEventListener('click', () => navigate(1), { signal });
      playButton?.addEventListener('click', togglePlayback, { signal });
      muteButton?.addEventListener('click', () => {
        const media = video();
        if (!media) return;
        muteChosen = true;
        mutedPreference = media.muted = !media.muted;
        syncControls();
      }, { signal });
      progress?.addEventListener('input', () => {
        const media = video();
        if (media && Number.isFinite(media.duration) && media.duration > 0) {
          media.currentTime = Math.max(0, Math.min(100, Number(progress.value))) / 100 * media.duration;
          syncControls();
        }
      }, { signal });
      desktop.addEventListener('change', () => {
        resetMotion();
        setMode();
      }, { signal });
      stage.addEventListener('click', (event) => {
        if (!reel || event.target.closest(interactive)) return;
        if (performance.now() < ignoreClickUntil) {
          event.preventDefault();
          return;
        }
        togglePlayback();
      }, { signal });
      stage.addEventListener('pointerdown', (event) => {
        if (!reel || !dialog.open || event.button !== 0 || event.target.closest(interactive)) return;
        pointers.add(event.pointerId);
        if (pointers.size > 1) {
          multiplePointers = true;
          gesture = null;
          video()?.style.removeProperty('transform');
          video()?.style.removeProperty('opacity');
          ignoreClickUntil = performance.now() + 350;
          return;
        }
        animation?.cancel();
        animation = null;
        stage.setPointerCapture(event.pointerId);
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, dy: 0, start: performance.now(), dragging: false };
      }, { signal });
      stage.addEventListener('pointermove', (event) => {
        if (!gesture || gesture.id !== event.pointerId || multiplePointers) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (!gesture.dragging && Math.max(Math.abs(dx), Math.abs(dy)) < 10) return;
        if (!gesture.dragging && Math.abs(dx) > Math.abs(dy)) {
          gesture = null;
          ignoreClickUntil = performance.now() + 350;
          return;
        }
        gesture.dragging = true;
        gesture.dy = dy;
        event.preventDefault();
        const media = video();
        if (media) {
          media.style.transform = `translate3d(0, ${dy}px, 0)`;
          media.style.opacity = String(1 - Math.min(0.35, Math.abs(dy) / Math.max(1, stage.clientHeight)));
        }
      }, { signal });
      const endPointer = (event) => {
        const active = gesture;
        if (multiplePointers) ignoreClickUntil = performance.now() + 350;
        pointers.delete(event.pointerId);
        if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
        if (active?.id === event.pointerId) {
          gesture = null;
          const distance = Math.abs(active.dy);
          const threshold = Math.max(48, Math.min(96, stage.clientHeight * 0.12));
          const commit = !multiplePointers && event.type === 'pointerup' && active.dragging &&
            (distance >= threshold || (distance >= 30 && performance.now() - active.start < 250));
          if (active.dragging) ignoreClickUntil = performance.now() + 350;
          if (commit && photos.length > 1) navigate(active.dy < 0 ? 1 : -1, active.dy);
          else {
            const media = video();
            media?.style.removeProperty('transform');
            media?.style.removeProperty('opacity');
          }
        }
        if (!pointers.size) multiplePointers = false;
      };
      stage.addEventListener('pointerup', endPointer, { signal });
      stage.addEventListener('pointercancel', endPointer, { signal });
      dialog.addEventListener('keydown', (event) => {
        if (event.defaultPrevented || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
        if (event.target.closest('video, audio, input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]')) return;
        const direction = { ArrowLeft: -1, ArrowRight: 1, ...(reel ? { ArrowUp: -1, ArrowDown: 1 } : {}) }[event.key];
        if (!direction) return;
        event.preventDefault();
        navigate(direction);
      }, { signal });
      dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-rpc-lightbox-close]')) {
          dialog.close();
          return;
        }
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      }, { signal });
      dialog.addEventListener('close', () => {
        if (!dialog.open) this.cleanupLightbox();
      }, { signal });
      signal.addEventListener('abort', () => this.cleanupLightbox(false), { once: true });
    }

    async loadRecommendations(container, signal) {
      try {
        const response = await fetch(container.dataset.rpcRecommendationsUrl, { signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = html.querySelector('[data-rpc-recommendations-list]');
        const target = container.querySelector('[data-rpc-recommendations-list]');
        if (!incoming?.querySelector('[data-rn-card]') || !target || signal.aborted || !container.isConnected) return;
        target.replaceChildren(...incoming.children);
        container.hidden = false;
        container.removeAttribute('data-rpc-recommendations-url');
        target.dispatchEvent(new CustomEvent('reise:products-loaded', { bubbles: true }));
      } catch (error) {
        if (error.name !== 'AbortError' && window.Shopify?.designMode) {
          console.info('Reise: as recomendações não puderam ser carregadas nesta prévia.');
        }
      }
    }
  });
}
